import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Car, 
  Plus, 
  Fuel, 
  DollarSign, 
  Gauge, 
  TrendingUp,
  BarChart3,
  Trash2,
  Edit,
  Calendar
} from 'lucide-react';

export default function FrotaPage() {
  const [veiculos, setVeiculos] = useState([]);
  const [abastecimentos, setAbastecimentos] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    veiculos_ativos: 0,
    gasto_mes: 0,
    km_percorridos: 0,
    media_consumo: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Modais
  const [modalVeiculo, setModalVeiculo] = useState(false);
  const [modalAbastecimento, setModalAbastecimento] = useState(false);
  const [editingVeiculo, setEditingVeiculo] = useState(null);
  
  // Form states
  const [formVeiculo, setFormVeiculo] = useState({
    modelo: '',
    placa: '',
    responsavel: '',
    km_atual: 0
  });
  
  const [formAbastecimento, setFormAbastecimento] = useState({
    veiculo_id: '',
    data: new Date().toISOString().split('T')[0],
    litros: '',
    valor: '',
    km: '',
    posto: '',
    observacao: ''
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [veiculosRes, abastRes, statsRes] = await Promise.all([
        axios.get(API + '/frota/veiculos', { headers: getAuthHeader() }),
        axios.get(API + '/frota/abastecimentos', { headers: getAuthHeader() }),
        axios.get(API + '/frota/estatisticas', { headers: getAuthHeader() })
      ]);
      setVeiculos(veiculosRes.data);
      setAbastecimentos(abastRes.data);
      setEstatisticas(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados da frota');
    } finally {
      setLoading(false);
    }
  };

  const salvarVeiculo = async () => {
    if (!formVeiculo.modelo || !formVeiculo.placa) {
      toast.error('Preencha modelo e placa');
      return;
    }
    
    try {
      if (editingVeiculo) {
        await axios.put(
          API + '/frota/veiculos/' + editingVeiculo.id,
          formVeiculo,
          { headers: getAuthHeader() }
        );
        toast.success('Veículo atualizado!');
      } else {
        await axios.post(API + '/frota/veiculos', formVeiculo, { headers: getAuthHeader() });
        toast.success('Veículo cadastrado!');
      }
      setModalVeiculo(false);
      setEditingVeiculo(null);
      setFormVeiculo({ modelo: '', placa: '', responsavel: '', km_atual: 0 });
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar veículo:', error);
      toast.error('Erro ao salvar veículo');
    }
  };

  const excluirVeiculo = async (id) => {
    if (!window.confirm('Deseja excluir este veículo?')) return;
    
    try {
      await axios.delete(API + '/frota/veiculos/' + id, { headers: getAuthHeader() });
      toast.success('Veículo excluído!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir veículo:', error);
      toast.error('Erro ao excluir veículo');
    }
  };

  const editarVeiculo = (veiculo) => {
    setEditingVeiculo(veiculo);
    setFormVeiculo({
      modelo: veiculo.modelo,
      placa: veiculo.placa,
      responsavel: veiculo.responsavel || '',
      km_atual: veiculo.km_atual || 0
    });
    setModalVeiculo(true);
  };

  const salvarAbastecimento = async () => {
    if (!formAbastecimento.veiculo_id || !formAbastecimento.litros || !formAbastecimento.valor || !formAbastecimento.km) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      await axios.post(API + '/frota/abastecimentos', {
        ...formAbastecimento,
        litros: parseFloat(formAbastecimento.litros),
        valor: parseFloat(formAbastecimento.valor),
        km: parseFloat(formAbastecimento.km)
      }, { headers: getAuthHeader() });
      toast.success('Abastecimento registrado!');
      setModalAbastecimento(false);
      setFormAbastecimento({
        veiculo_id: '',
        data: new Date().toISOString().split('T')[0],
        litros: '',
        valor: '',
        km: '',
        posto: '',
        observacao: ''
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao registrar abastecimento:', error);
      toast.error('Erro ao registrar abastecimento');
    }
  };

  const excluirAbastecimento = async (id) => {
    if (!window.confirm('Deseja excluir este abastecimento?')) return;
    
    try {
      await axios.delete(API + '/frota/abastecimentos/' + id, { headers: getAuthHeader() });
      toast.success('Abastecimento excluído!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir abastecimento:', error);
      toast.error('Erro ao excluir abastecimento');
    }
  };

  const formatarData = (data) => {
    if (!data) return '';
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Car className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Gestão de Frota</h1>
            <p className="text-slate-500 text-sm">Controle de veículos, abastecimentos e consumo</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setEditingVeiculo(null);
              setFormVeiculo({ modelo: '', placa: '', responsavel: '', km_atual: 0 });
              setModalVeiculo(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="btn-novo-veiculo"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Veículo
          </Button>
          <Button
            onClick={() => setModalAbastecimento(true)}
            variant="outline"
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
            data-testid="btn-registrar-abastecimento"
          >
            <Fuel className="w-4 h-4 mr-2" />
            Registrar Abastecimento
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="veiculos" className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            Veículos ({veiculos.length})
          </TabsTrigger>
          <TabsTrigger value="abastecimentos" className="flex items-center gap-2">
            <Fuel className="w-4 h-4" />
            Abastecimentos
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Veículos Ativos</p>
                    <p className="text-3xl font-bold text-slate-800">{estatisticas.veiculos_ativos}</p>
                  </div>
                  <Car className="w-10 h-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Gasto do Mês</p>
                    <p className="text-2xl font-bold text-green-600">{formatarValor(estatisticas.gasto_mes)}</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Km Percorridos (Mês)</p>
                    <p className="text-3xl font-bold text-slate-800">{estatisticas.km_percorridos} km</p>
                  </div>
                  <Gauge className="w-10 h-10 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Média Consumo (Mês)</p>
                    <p className="text-3xl font-bold text-slate-800">{estatisticas.media_consumo} km/L</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Veículos Tab */}
        <TabsContent value="veiculos">
          <Card>
            <CardContent className="p-6">
              {veiculos.length === 0 ? (
                <div className="text-center py-12">
                  <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum veículo cadastrado</p>
                  <Button
                    onClick={() => setModalVeiculo(true)}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Cadastrar Veículo
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {veiculos.map((veiculo) => (
                    <div
                      key={veiculo.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`veiculo-${veiculo.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Car className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{veiculo.modelo}</p>
                          <p className="text-sm text-slate-500">
                            {veiculo.placa} {veiculo.responsavel && `• ${veiculo.responsavel}`}
                          </p>
                          <p className="text-xs text-slate-400">Km: {veiculo.km_atual || 0}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => editarVeiculo(veiculo)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => excluirVeiculo(veiculo.id)}
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Abastecimentos Tab */}
        <TabsContent value="abastecimentos">
          <Card>
            <CardContent className="p-6">
              {abastecimentos.length === 0 ? (
                <div className="text-center py-12">
                  <Fuel className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum abastecimento registrado</p>
                  <Button
                    onClick={() => setModalAbastecimento(true)}
                    className="mt-4 bg-orange-500 hover:bg-orange-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Registrar Abastecimento
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {abastecimentos.map((abast) => (
                    <div
                      key={abast.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`abastecimento-${abast.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Fuel className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">
                            {abast.veiculo_modelo || 'Veículo'} <span className="text-slate-500 font-normal">{abast.veiculo_placa}</span>
                          </p>
                          <p className="text-sm text-slate-500">
                            {formatarData(abast.data)} • {abast.litros}L • {formatarValor(abast.valor)} • {abast.km} km
                          </p>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => excluirAbastecimento(abast.id)}
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Novo Veículo */}
      <Dialog open={modalVeiculo} onOpenChange={setModalVeiculo}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingVeiculo ? 'Editar Veículo' : 'Novo Veículo'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Modelo *</Label>
              <Input
                value={formVeiculo.modelo}
                onChange={(e) => setFormVeiculo({ ...formVeiculo, modelo: e.target.value })}
                placeholder="Ex: Fiat Palio"
                data-testid="input-modelo"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Placa *</Label>
              <Input
                value={formVeiculo.placa}
                onChange={(e) => setFormVeiculo({ ...formVeiculo, placa: e.target.value.toUpperCase() })}
                placeholder="Ex: ABC1234"
                maxLength={7}
                data-testid="input-placa"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Responsável</Label>
              <Input
                value={formVeiculo.responsavel}
                onChange={(e) => setFormVeiculo({ ...formVeiculo, responsavel: e.target.value })}
                placeholder="Nome do motorista/responsável"
                data-testid="input-responsavel"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Km Atual</Label>
              <Input
                type="number"
                value={formVeiculo.km_atual}
                onChange={(e) => setFormVeiculo({ ...formVeiculo, km_atual: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                data-testid="input-km-atual"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalVeiculo(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarVeiculo} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-veiculo">
              {editingVeiculo ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Registrar Abastecimento */}
      <Dialog open={modalAbastecimento} onOpenChange={setModalAbastecimento}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Abastecimento</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Veículo *</Label>
              <select
                className="w-full p-2 border rounded-md bg-white"
                value={formAbastecimento.veiculo_id}
                onChange={(e) => setFormAbastecimento({ ...formAbastecimento, veiculo_id: e.target.value })}
                data-testid="select-veiculo"
              >
                <option value="">Selecione um veículo</option>
                {veiculos.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.modelo} - {v.placa}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formAbastecimento.data}
                  onChange={(e) => setFormAbastecimento({ ...formAbastecimento, data: e.target.value })}
                  data-testid="input-data"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Km Atual *</Label>
                <Input
                  type="number"
                  value={formAbastecimento.km}
                  onChange={(e) => setFormAbastecimento({ ...formAbastecimento, km: e.target.value })}
                  placeholder="0"
                  data-testid="input-km"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Litros *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formAbastecimento.litros}
                  onChange={(e) => setFormAbastecimento({ ...formAbastecimento, litros: e.target.value })}
                  placeholder="0.00"
                  data-testid="input-litros"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Valor (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formAbastecimento.valor}
                  onChange={(e) => setFormAbastecimento({ ...formAbastecimento, valor: e.target.value })}
                  placeholder="0.00"
                  data-testid="input-valor"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Posto</Label>
              <Input
                value={formAbastecimento.posto}
                onChange={(e) => setFormAbastecimento({ ...formAbastecimento, posto: e.target.value })}
                placeholder="Nome do posto (opcional)"
                data-testid="input-posto"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAbastecimento(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarAbastecimento} className="bg-orange-500 hover:bg-orange-600" data-testid="btn-salvar-abastecimento">
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
